import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
    Modal,
    Button,
    TextInput,
    Box,
    Alert,
    Loader,
    Text,
    Paper,
    Tabs,
    Tooltip,
    Group,
    Stack,
    SimpleGrid,
    Grid,
    ActionIcon,
} from '@mantine/core';
import { IconBug } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useForm } from '@mantine/form';
import { MODAL_SIZES } from '../../theme/mantineTheme';
import { useModalFullscreen } from '../../hooks/useModalFullscreen';
import { entityService, userService } from '../../services/entityServiceSingleton';
import { getSingularLabel } from '../../utils/pluralizationUtils';
import { useFieldOptionsForEntityType } from '../../store/schemaHooks';
import { store } from '../../store/store';
import { getFieldOptionsForEntityType } from '../../store/schemaSelectors';
import { JsonViewer } from '../common/JsonViewer';
import { SharedDebugComponent } from '../common/SharedDebugComponent';
import EntityBrowserDialog from './EntityBrowserDialog';
import { useEntityTypes } from '../../contexts/EntityTypesContext';
import { useViewModeSafe } from '../../contexts/ViewModeContext';
import EntitlementsService from '../../services/EntitlementsService';
import {
    getSettableAttributeIdsFromTargetResponse,
    getNonIndexedHasLengthCountAttributeNamesFromTargetResponse,
    getLargeArrayRefAttributeNamesFromTargetResponse,
    getDeferredArrayRefAttributeNamesFromTargetResponse,
    buildFormAttributesFromTier2Attributes,
    buildFormAttributesFromTier2AttributeBindings,
    getTier2AttributesFromResponse,
    isEntitySetFacade,
    getReadOnlyAttributeNamesFromTier2,
    getReadOnlyAttributeNamesFromTargetResponse,
    getDisjointedEntityIds,
    type EntityEntitlementForTargetResponse,
} from '../../types/EntityEntitlements';
// patchOperationUtils now used internally by useEntitySave hook
import { extractReferenceId, extractReferenceDisplayName } from '../../utils/referenceUtils';
import { getEntityTypeFlags } from '../../utils/entityTypeDetection';
import { clearContentBodyHashParam } from '../../utils/hashQueryParams';
import { createEntityRef, getEntityValueFromARef, setEntityValueFromARef, normalizeKey, type EntityRef } from '../../types';
import { NON_INDEXED_PROPERTY_STUB, isARef } from '../../utils/entityPropertyUtils';
import { getWorkflowConfigPayload } from '../../utils/workflowConfigUtils';
import { getAttributeName } from '../../utils/schemaAttributeUtils';
// formValidationUtils now used internally by useEntitySave hook
import { getEntityValidationWarnings } from '../../validation';
import { useReferenceAttributeLoader } from '../../hooks/useReferenceAttributeLoader';
import { usePolicyRuleSetScopes } from '../../hooks/usePolicyRuleSetScopes';
import { useReferenceFieldDisplay } from '../../hooks/useReferenceFieldDisplay';
import ContentTypesCacheService from '../../services/ContentTypesCacheService';
import { normalizeFormDataForPatchComparison, useEntitySave } from '../../hooks/useEntitySave';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import { useIsImperator } from '../../hooks/useIsImperator';
import { usePerEntityTableEntitlement } from '../../hooks/usePerEntityTableEntitlement';
import { filterRequestedAttributesByEntitlements, getDefaultRequestedAttributesForEntityType } from '../../utils/tableAttributesUtils';
import { PolicyRuleFormLayout, EmailTemplateFormLayout, SetFormLayout, PersonFormLayout, GroupFormLayout, RequestApprovalTypeFormLayout } from '../entities/fieldRenderers';
import type { FieldDebugTooltipContext } from '../entities/entityFieldDebugUtils';
import { FieldWithDebugTooltip } from '../entities/FieldWithDebugTooltip';
import { EntityUpsertForm } from '../entities/EntityUpsertForm';
import { ShadcnDetailViewLayout } from '../entities/ShadcnDetailViewLayout';
import type { FormAttribute } from '../entities/EntityUpsertForm.types';
import { renderEntityUpsertFormField } from '../entities/renderEntityUpsertFormField';
import { entityUpsertRenderField } from '../entities/entityUpsertRenderField/createEntityUpsertRenderField';
import { EntityUpsertSessionProvider } from '../entities/entityUpsertRenderField/EntityUpsertSessionContext';
import { useEntityUpsertSessionValue } from '../entities/entityUpsertRenderField/useEntityUpsertSessionValue';
import { UnifiedRawViewsTabs, type UnifiedRawViewsTabsProps } from '../entities/UnifiedRawViewsTabs';
import { usePaginatedArrayRefViewer } from '../../hooks/usePaginatedArrayRefViewer';
import { useEntityUpsertTier3 } from '../../hooks/useEntityUpsertTier3';
import { useEntityFormSchema } from '../../hooks/useEntityFormSchema';
import { useEntityUpsertTier2 } from '../../hooks/useEntityUpsertTier2';
import { useEncryptedFieldState } from '../../hooks/useEncryptedFieldState';
import { PaginatedArrayRefViewerModal } from './PaginatedArrayRefViewerModal';
import { EntityUpsertReferenceBrowsers } from './EntityUpsertReferenceBrowsers';
import { EntitlementViewer } from '../entities/EntitlementViewer';
import { ShadcnUnsavedChangesDialog } from './ShadcnUnsavedChangesDialog';
import { EntityDialogActions } from './EntityDialogActions';
import { DialogErrorAlert } from './DialogErrorAlert';
import { FilterPreviewDialog } from './FilterPreviewDialog';
import { EnumeratedSetManualMemberBlockedModal } from './EnumeratedSetManualMemberBlockedModal';
import { PossessiveTypeWipeConfirmDialog } from './PossessiveTypeWipeConfirmDialog';

interface GenericEntityUpsertDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: (createdEntity: any) => void;
    entityType?: string;
    entityId?: string | null;
    existingEntity?: any; // Optional existing entity data for edit mode
    /** When opening from read-view (e.g. EntityDetailDialog), pass tier3 entitlement to avoid re-fetching. */
    targetEntitlement?: EntityEntitlementForTargetResponse | null;
    /** Legacy dialog uses modal presentation; the unified entity screen reuses the same content inline. */
    presentation?: 'dialog' | 'inline';
    /** Overrides the screen mode while still reusing the edit-backed entity loading path. */
    surfaceMode?: 'create' | 'edit' | 'view';
    /** Allows the unified screen to toggle view/edit in place without reloading the form state. */
    onSurfaceModeChange?: (mode: 'view' | 'edit') => void;
    /** When true, the parent surface renders inline navigation controls (Back/Edit/View Mode). */
    inlineNavigationOwner?: 'dialog' | 'parent';
    /** Optional escape hatch from the legacy edit dialog into the new unified screen. */
    onOpenNewUi?: () => void;
    /** Extra tabs appended to the shared tab strip (used by the unified screen for legacy detail tabs). */
    supplementalTabs?: Array<{
        key?: string;
        label: React.ReactNode;
        content: React.ReactNode;
        leftSection?: React.ReactNode;
        onActivate?: () => void;
    }>;
    /** UnifiedEntityScreen: parent useEntityDetailLoader loads the entity; skip duplicate getEntityById in schema init. */
    parentProvidesEntityLoad?: boolean;
    /** Called when a Content inter-document link is clicked in the markdown preview. */
    onContentLinkClick?: (entityId: string, displayName: string) => void;
    /** Called when the inline editor's unsaved state changes (replaces the window event). */
    onUnsavedStateChange?: (state: { active: boolean; hasUnsavedChanges: boolean; mode: 'create' | 'edit'; entityType: string }) => void;
}

const RAW_VIEWS_DERIVATION_DEBOUNCE_MS = 200;
const VALIDATION_DEBOUNCE_MS = 200;

/** Compact layout for Content metadata fields with Body taking full width below. */
function ContentCompactLayout({
    schemaAttributes,
    formData,
    onFieldChange,
    renderField,
    disabled: _disabled,
    nonIndexedStubValue: _nonIndexedStubValue,
    onRequestAttributeValue: _onRequestAttributeValue,
    loadingAttribute: _loadingAttribute,
}: {
    schemaAttributes: FormAttribute[];
    formData: Record<string, any>;
    onFieldChange: (fieldName: string, value: any) => void;
    renderField: (attr: FormAttribute, value: unknown, onChange: (value: unknown) => void) => React.ReactNode;
    disabled: boolean;
    nonIndexedStubValue: string;
    onRequestAttributeValue?: (name: string) => Promise<unknown | null>;
    loadingAttribute?: string | null;
}): React.ReactElement {
    const findAttr = (key: string) => schemaAttributes.find(a => {
        const n = (a.name ?? a.id ?? '').toString().toLowerCase().replace(/^\$\./, '');
        return n === key;
    });
    const idAttr = findAttr('id');
    const displayNameAttr = findAttr('displayname');
    const subjectAttr = findAttr('subject');
    const contentTypeAttr = findAttr('contenttype');
    const tagsAttr = findAttr('tags');
    const bodyAttr = findAttr('body');

    const knownKeys = new Set(['id', 'displayname', 'subject', 'contenttype', 'tags', 'body']);
    const otherAttrs = schemaAttributes.filter(a => {
        const n = (a.name ?? a.id ?? '').toString().toLowerCase().replace(/^\$\./, '');
        return !knownKeys.has(n);
    });

    const renderOneField = (attr: FormAttribute) => {
        const name = getAttributeName(attr);
        const value = name ? getEntityValueFromARef(name, formData as Record<string, unknown>) : '';
        const handleChange = (v: unknown) => onFieldChange(name, v);
        return renderField(attr, value ?? '', handleChange);
    };

    return (
        <Box>
            {idAttr && renderOneField(idAttr)}
            <Grid gutter="md" mb="xs">
                <Grid.Col span={{ base: 12, sm: 6 }}>{displayNameAttr && renderOneField(displayNameAttr)}</Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6 }}>{subjectAttr && renderOneField(subjectAttr)}</Grid.Col>
            </Grid>
            <Grid gutter="md" mb="xs">
                <Grid.Col span={{ base: 12, sm: 3 }}>{contentTypeAttr && renderOneField(contentTypeAttr)}</Grid.Col>
                <Grid.Col span={{ base: 12, sm: 9 }}>{tagsAttr && renderOneField(tagsAttr)}</Grid.Col>
            </Grid>
            {otherAttrs.map((attr, i) => (
                <Box key={(attr.name ?? attr.id ?? `other-${i}`).toString()}>
                    {renderOneField(attr)}
                </Box>
            ))}
            {bodyAttr && renderOneField(bodyAttr)}
        </Box>
    );
}

const GenericEntityUpsertDialog: React.FC<GenericEntityUpsertDialogProps> = ({
    open,
    onClose,
    onSuccess,
    entityType: propEntityType,
    entityId,
    existingEntity,
    targetEntitlement: targetEntitlementProp,
    presentation = 'dialog',
    surfaceMode,
    onSurfaceModeChange,
    inlineNavigationOwner = 'dialog',
    onOpenNewUi: _onOpenNewUi,
    supplementalTabs = [],
    parentProvidesEntityLoad = false,
    onContentLinkClick,
    onUnsavedStateChange,
}) => {
    const { isStandard: isStandardViewMode } = useViewModeSafe();
    const modalFs = useModalFullscreen(MODAL_SIZES.detail);
    const isImperator = useIsImperator();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Mantine useForm replaces raw useState for form data + original data tracking
    const form = useForm<Record<string, any>>({ initialValues: {} });
    const formData = form.values;
    const setFormData: React.Dispatch<React.SetStateAction<Record<string, any>>> = useCallback(
        (action: React.SetStateAction<Record<string, any>>) => {
            if (typeof action === 'function') {
                form.setValues((prev) => (action as (prev: Record<string, any>) => Record<string, any>)(prev));
            } else {
                form.setValues(action);
            }
        },
        [form],
    );
    // originalData: tracked separately since Mantine's initialValues/resetDirty
    // cannot replicate the custom normalizeForCompare dirty logic
    const [originalData, setOriginalData] = useState<Record<string, any> | null>(null);
    // Track raw input strings for multi-valued fields (to allow typing commas)
    const [multiValuedRawInputs, setMultiValuedRawInputs] = useState<Record<string, string>>({});
    /** Field name when the multi-valued reference entity browser is open (e.g. MemberOf). */
    const [multiRefBrowserField, setMultiRefBrowserField] = useState<string | null>(null);
    /** For ManualSetMember (Set): entity types allowed after schema Entities check. null = resolving, [] = none, [type] = allowed. */
    const [manualMemberAllowedTypes, setManualMemberAllowedTypes] = useState<string[] | null>(null);
    const [activeTab, setActiveTab] = useState(0);
    const [rawViewsFormDataSnapshot, setRawViewsFormDataSnapshot] = useState<Record<string, any> | null>(null);
    const [validationFormDataSnapshot, setValidationFormDataSnapshot] = useState<Record<string, any>>({});
    /** For PSGroup (RequestApprovalType and PluginFunction): which entity type's tier 2 is shown in Ent tab (default: current entity type). */
    const [fullEntityData, setFullEntityData] = useState<any>(null); // Full entity data for Raw Views
    /** Form state uses plain keys (no $. prefix) so we have one key per field; schema may use "$.RequestorSet" etc. */
    const formKeyFromAttrName = useCallback((n: string) => (n ?? '').toString().trim().replace(/^\$\./, '') || (n ?? ''), []);
    /** Snapshot of entity as loaded from fetch (for field debug tooltips). Cleared when dialog closes. */
    const rawEntitySnapshotRef = useRef<Record<string, any> | null>(null);
    const [showDebugTabs, setShowDebugTabs] = useState(false);
    /** When true, show an info icon next to each field with tooltip (raw value + fetch status). */
    const [showFieldDebugInfo, setShowFieldDebugInfo] = useState(false);
    // Reset activeTab and raw inputs when dialog opens; clear debug snapshot and decrypt reveal when dialog closes
    useEffect(() => {
        if (!open) {
            rawEntitySnapshotRef.current = null;
            setFullEntityData(null);
            setRawViewsFormDataSnapshot(null);
            setValidationFormDataSnapshot({});
            setDecryptReveal(null);
        }
        if (open) {
            setActiveTab(0);
            setValidationFormDataSnapshot(formData);
            setMultiValuedRawInputs({}); // Clear raw inputs when dialog opens
        }
    }, [open, propEntityType]);

    const [actionWorkflowBrowserOpen, setActionWorkflowBrowserOpen] = useState(false);
    const [selectedActionWorkflow, setSelectedActionWorkflow] = useState<any>(null);
    const [actionWorkflowError, setActionWorkflowError] = useState<string | null>(null);

    // PolicyRule-specific state (remaining state not covered by hooks)
    const [relativeTargetAttributeError, setRelativeTargetAttributeError] = useState<string | null>(null);

    // RequestApprovalType specific state (browser dialogs only - rest in hook)
    const [attributeRefBrowserField, setAttributeRefBrowserField] = useState<string | null>(null);
    const [actorsAttributeBrowserOpen, setActorsAttributeBrowserOpen] = useState(false);
    /** Centralized email template list for RequestApprovalType (one load shared by all 4 email template fields) */
    const [emailTemplatesList, setEmailTemplatesList] = useState<any[]>([]);
    const [loadingEmailTemplates, setLoadingEmailTemplates] = useState(false);
    /** Attribute name currently being loaded (for unindexed "Load" in EntityUpsertForm). */
    const [loadingAttribute, setLoadingAttribute] = useState<string | null>(null);
    /** When set, the "Add items" entity browser is open for this large array-ref attribute. */
    const [deferredArrayAddField, setDeferredArrayAddField] = useState<string | null>(null);
    /** Pending entities to add per large array-ref attribute (for edit payload). */
    const [deferredArrayPendingAdds, setDeferredArrayPendingAdds] = useState<Record<string, Array<{ Id: string; DisplayName?: string; EntityType?: string }>>>({});
    /** Pending entity ids to remove per large array-ref attribute (for edit payload). */
    const [deferredArrayPendingRemoves, setDeferredArrayPendingRemoves] = useState<Record<string, string[]>>({});
    /** Expanded refs from existing entity; passed to useReferenceFieldDisplay so it doesn't call getEntityById(..., true) for each ref. */
    const [initialReferenceEntitiesFromEntity, setInitialReferenceEntitiesFromEntity] = useState<Record<string, any>>({});
    const {
        decryptReveal,
        setDecryptReveal,
        revealedEncryptedFieldNames,
        setRevealedEncryptedFieldNames,
        encryptedFieldsClearOnSave,
        setEncryptedFieldsClearOnSave,
        encryptedCreateVisible,
        setEncryptedCreateVisible,
        encryptedEditStoredVisible,
        setEncryptedEditStoredVisible,
        encryptedViewDecryptedByName,
        setEncryptedViewDecryptedByName,
        encryptedViewPlainVisible,
        setEncryptedViewPlainVisible,
        revealedDecryptedCacheRef,
    } = useEncryptedFieldState();
    /** Set/Group filter preview (FilterPreviewDialog). */
    const [filterPreviewOpen, setFilterPreviewOpen] = useState(false);
    const [filterPreviewScope, setFilterPreviewScope] = useState('');
    const [filterPreviewFilter, setFilterPreviewFilter] = useState('');
    const [filterPreviewAttributes, setFilterPreviewAttributes] = useState<string[] | undefined>(undefined);
    const [filterPreviewTitleOverride, setFilterPreviewTitleOverride] = useState<string | undefined>(undefined);
    const [filterPreviewNoteMessage, setFilterPreviewNoteMessage] = useState<string | undefined>(undefined);

    /** RequestApprovalType: when user switches Type to non-Possessive (e.g. Generic) and possessive fields have data, show confirm before wiping */
    const [possessiveWipeConfirmOpen, setPossessiveWipeConfirmOpen] = useState(false);
    const [possessiveWipeConfirmPending, setPossessiveWipeConfirmPending] = useState<string | null>(null);

    // When dialog opens: show loading until schema init completes. When it closes: reset state so next open shows loading instead of a flash of "No editable fields".
    useEffect(() => {
        if (!open) {
            setDecryptReveal(null);
            setRevealedEncryptedFieldNames([]);
            setEncryptedFieldsClearOnSave([]);
            setEncryptedCreateVisible({});
            setEncryptedEditStoredVisible({});
            setEncryptedViewDecryptedByName({});
            setEncryptedViewPlainVisible({});
            revealedDecryptedCacheRef.current = {};
            setLoadingAttribute(null);
            setFilterPreviewOpen(false);
            setFilterPreviewScope('');
            setFilterPreviewFilter('');
            setFilterPreviewAttributes(undefined);
            setFilterPreviewTitleOverride(undefined);
            setFilterPreviewNoteMessage(undefined);
            setDeferredArrayAddField(null);
            setDeferredArrayPendingAdds({});
            setDeferredArrayPendingRemoves({});
            setPossessiveWipeConfirmOpen(false);
            setPossessiveWipeConfirmPending(null);
        }
    }, [open]);

    const contentTypesCacheService = useMemo(() => ContentTypesCacheService.getInstance(), []);
    const { allEntityTypes, entityTypes: entityTypesFromContext } = useEntityTypes();
    const disjointedEntityTypes = useMemo(() => {
        const cached = EntitlementsService.getCachedEntityEntitlements();
        const ids = getDisjointedEntityIds(cached ?? null);
        return Array.from(ids);
    }, []);

    // Extract entity type from props
    // The entityType prop comes from EntityActionHandler.executeAction
    // It should match the entity type registered in EntityActionInitializer
    const entityType = propEntityType || '';

    // Get field options from Redux (after entityType is defined)
    const entityTypeFieldOptions = useFieldOptionsForEntityType(entityType);

    if (!entityType) {
        console.error('GenericEntityUpsertDialog: entityType prop is required');
    }

    const entityLabel = getSingularLabel(entityType);
    const isEditMode = !!entityId;

    const { targetEntitlement, tier3Settled, tier3EntitlementError, tier3ResultRef } = useEntityUpsertTier3({
        open,
        entityId,
        entityType,
        isEditMode,
        targetEntitlementProp,
    });

    const nonIndexedHasLengthCountNamesSet = useMemo(() => {
        const hasLength = getNonIndexedHasLengthCountAttributeNamesFromTargetResponse(targetEntitlement ?? null);
        const largeArrayRef = getLargeArrayRefAttributeNamesFromTargetResponse(targetEntitlement ?? null);
        return new Set([...hasLength, ...largeArrayRef].map((id: string) => normalizeKey(id)));
    }, [targetEntitlement]);

    const largeArrayRefNamesSet = useMemo(() => {
        const names = getLargeArrayRefAttributeNamesFromTargetResponse(targetEntitlement ?? null);
        return new Set(names.map((id: string) => normalizeKey(id)));
    }, [targetEntitlement]);

    const deferredArrayRefNamesSet = useMemo(() => {
        const names = getDeferredArrayRefAttributeNamesFromTargetResponse(targetEntitlement ?? null);
        return new Set(names.map((id: string) => normalizeKey(id)));
    }, [targetEntitlement]);

    const isEmptyForDeferLoad = useCallback(
        (v: unknown) => v == null || v === '' || (Array.isArray(v) && v.length === 0),
        []
    );

    const effectiveSurfaceMode = surfaceMode ?? (entityId ? 'edit' : 'create');
    const isViewSurface = effectiveSurfaceMode === 'view';
    const isInlinePresentation = presentation === 'inline';
    const parentOwnsInlineNavigation = isInlinePresentation && inlineNavigationOwner === 'parent';
    const baseTabCount = isEditMode ? 3 : (existingEntity && !entityId ? 4 : 3);
    const isPayloadDebugTabActive = activeTab === 2;
    const handlePrimaryTabChange = useCallback((value: string | null) => {
        const nextTab = Number(value ?? 0);
        setActiveTab(nextTab);
        const supplementalTab = supplementalTabs[nextTab - baseTabCount];
        supplementalTab?.onActivate?.();
    }, [baseTabCount, supplementalTabs]);

    useEffect(() => {
        if (!open || !isPayloadDebugTabActive) {
            setRawViewsFormDataSnapshot(null);
            return;
        }

        const timeoutId = window.setTimeout(() => {
            setRawViewsFormDataSnapshot(formData);
        }, RAW_VIEWS_DERIVATION_DEBOUNCE_MS);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [open, isPayloadDebugTabActive, formData]);

    useEffect(() => {
        if (!open) {
            setValidationFormDataSnapshot(formData);
            return;
        }

        const timeoutId = window.setTimeout(() => {
            setValidationFormDataSnapshot(formData);
        }, VALIDATION_DEBOUNCE_MS);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [open, formData]);

    // Get entity type flags (isPerson uses tier1 cached entitlements EntitySets.Person)
    const entityTypeFlags = getEntityTypeFlags(entityType);
    const {
        isSetTransition,
        isEmailTemplate,
        isRequestApprovalType,
        isPolicyRule,
        isSet,
        isPerson,
        isContent,
        isFile
    } = entityTypeFlags;

    const {
        paginatedArrayRefViewer,
        setPaginatedArrayRefViewer,
        viewerEntities,
        viewerLoading,
        viewerError,
        viewerPage,
        setViewerPage,
        viewerTotalPages,
        viewerTotalResults,
        viewerSelectedIds,
        setViewerSelectedIds,
        viewerSelectedEntities,
        setViewerSelectedEntities,
        viewerExtraColumnAttributes,
        handleViewerEntitySelect,
        handleViewerSelectAll,
        closeViewer,
    } = usePaginatedArrayRefViewer({
        open,
        entityType,
        entityId,
        formData,
        isSet,
        targetEntitlement: targetEntitlement ?? null,
        entityService,
    });

    // When ManualSetMember browser is open for a Set, resolve allowed types from FilterScope + schema Entities check
    useEffect(() => {
        if (multiRefBrowserField !== 'ManualSetMember' || !isSet) {
            setManualMemberAllowedTypes(null);
            return;
        }
        const filterScope = (getEntityValueFromARef('FilterScope', formData ?? {}) ?? '').toString().trim();
        if (!filterScope) {
            setManualMemberAllowedTypes([]);
            return;
        }
        let cancelled = false;
        entityService.getSchemaEntity(filterScope).then((schema) => {
            if (cancelled) return;
            const hasEntities = schema != null && Object.prototype.hasOwnProperty.call(schema, 'Entities');
            setManualMemberAllowedTypes(hasEntities ? [filterScope] : []);
        }).catch(() => {
            if (!cancelled) setManualMemberAllowedTypes([]);
        });
        return () => { cancelled = true; };
    }, [multiRefBrowserField, isSet, formData, entityService]);

    const isGroup = entityType?.toLowerCase() === 'group';
    const isPluginFunction = entityType?.toLowerCase() === 'pluginfunction';

    const {
        tier2Entitlement,
        tier2PossessiveScopeEntitlement,
        tier2PossessiveTargetAttributeEntitlement,
        loadingPossessiveScopeTier2,
        loadingPossessiveTargetAttributeTier2,
        tier2EntitlementError,
        tier2PossessiveScopeError,
        tier2PossessiveTargetError,
        possessiveScope,
        possessiveTargetAttributeKey,
        referenceAttributesFromTier2,
        referenceAttributesFromPossessiveScope,
        referenceAttributesFromPossessiveTargetAttribute,
        attributesFromPossessiveTargetAttributeTier2,
        entTier2SelectedEntityType,
        setEntTier2SelectedEntityType,
        entTier2TypesInPlay,
        entTier2Resolved,
        currentEntityTypeKey,
    } = useEntityUpsertTier2({
        open,
        entityType,
        propEntityType,
        formData,
        isRequestApprovalType,
        isPluginFunction,
    });

    // Use hook for reference attribute loading (RequestApprovalType)
    const {
        allReferenceAttributes,
        loadingAllReferenceAttributes,
        targetEntityTypeReferenceAttributes,
        loadingReferenceAttributes,
        selectedReferenceAttributeInfo,
        lastFetchedAttributeRef
    } = useReferenceAttributeLoader({
        isRequestApprovalType,
        open,
        possessiveTargetAttribute: getEntityValueFromARef('PossessiveTargetAttribute', formData),
        onActorsAttributeUpdate: (value: string) => {
            setFormData(prev => ({ ...prev, PossessiveActorsAttribute: value }));
        },
        existingActorsValue: getEntityValueFromARef('PossessiveActorsAttribute', formData)
    });

    // Centralized email template load for RequestApprovalType (one fetch shared by CompletedApprovalEmail, PendingApprovalEmail, RejectedRequestEmail, ExpiredEmail)
    useEffect(() => {
        if (!open || !isRequestApprovalType) {
            if (!open) {
                setEmailTemplatesList([]);
                setLoadingEmailTemplates(false);
            }
            return;
        }
        let cancelled = false;
        setLoadingEmailTemplates(true);
        const loadAll = async () => {
            try {
                let allTemplates: any[] = [];
                let currentPage = 1;
                let hasMore = true;
                const pageSize = 100;
                while (hasMore && !cancelled) {
                    const response = await entityService.getEntitiesByType('EmailTemplate', currentPage, pageSize);
                    const pageTemplates = response.data || [];
                    allTemplates = [...allTemplates, ...pageTemplates];
                    const totalResults = response.totalResults || 0;
                    const totalPages = Math.ceil(totalResults / pageSize);
                    hasMore = currentPage < totalPages;
                    currentPage++;
                }
                if (cancelled) return;
                allTemplates.sort((a, b) => {
                    const nameA = (a.DisplayName || a.Id || '').toLowerCase();
                    const nameB = (b.DisplayName || b.Id || '').toLowerCase();
                    return nameA.localeCompare(nameB);
                });
                setEmailTemplatesList(allTemplates);
            } catch (e) {
                if (!cancelled) setEmailTemplatesList([]);
            } finally {
                if (!cancelled) setLoadingEmailTemplates(false);
            }
        };
        loadAll();
        return () => { cancelled = true; };
    }, [open, isRequestApprovalType, entityService]);

    // Use hook for PolicyRule set scopes
    const targetSetId = extractReferenceId(getEntityValueFromARef('TargetSet', formData));
    const requestorSetId = extractReferenceId(getEntityValueFromARef('RequestorSet', formData));

    const {
        targetSetFilterScope,
        requestorSetFilterScope,
        setEntitiesCache: setEntitiesCacheRef,
        handleTargetSetChange: hookHandleTargetSetChange,
        handleRequestorSetChange: hookHandleRequestorSetChange,
        fetchTargetSetFilterScope,
        fetchRequestorSetFilterScope
    } = usePolicyRuleSetScopes({
        isPolicyRule,
        open,
        targetSetId,
        requestorSetId,
        entityService
    });

    const { schemaAttributes: rawSchemaAttributes, schemaLoading, schemaEntityBindings } = useEntityFormSchema({
        open,
        entityType,
        entityId,
        isEditMode,
        existingEntity,
        entityTypeFieldOptions,
        targetEntitlement: targetEntitlement ?? null,
        targetEntitlementProp,
        tier3Settled,
        tier3ResultRef,
        entityService,
        showFieldDebugInfo,
        formKeyFromAttrName,
        isRequestApprovalType,
        isPluginFunction,
        isContent,
        isEmailTemplate,
        isFile,
        isPolicyRule,
        isSet,
        isSetTransition,
        parentProvidesEntityLoad,
        init: {
            setFormData,
            setOriginalData,
            setFullEntityData,
            setInitialReferenceEntitiesFromEntity,
            setMultiValuedRawInputs,
            setError,
            rawEntitySnapshotRef,
            fetchTargetSetFilterScope,
        },
    });

    const schemaAttributes = useMemo(() => {
        if (isStandardViewMode) {
            return rawSchemaAttributes.filter(attr => {
                const name = (attr?.name ?? attr?.id ?? '').toLowerCase();
                return name !== 'id';
            });
        }
        return rawSchemaAttributes;
    }, [rawSchemaAttributes, isStandardViewMode]);

    // Sync Body from parent-loaded entity into formData when it transitions from stub to real content
    useEffect(() => {
        if (!parentProvidesEntityLoad || !existingEntity || typeof existingEntity !== 'object') return;
        const entity = existingEntity as Record<string, unknown>;
        const bodyKey = Object.keys(entity).find(k => k.toLowerCase() === 'body');
        if (!bodyKey) return;
        const bodyValue = entity[bodyKey];
        if (bodyValue == null || bodyValue === NON_INDEXED_PROPERTY_STUB) return;
        setFormData(prev => {
            const prevBodyKey = Object.keys(prev).find(k => k.toLowerCase() === 'body');
            const prevBody = prevBodyKey ? prev[prevBodyKey] : undefined;
            if (prevBody === NON_INDEXED_PROPERTY_STUB || prevBody === undefined) {
                const key = prevBodyKey ?? bodyKey;
                return { ...prev, [key]: bodyValue };
            }
            return prev;
        });
        setOriginalData(prev => {
            if (!prev) return prev;
            const prevBodyKey = Object.keys(prev).find(k => k.toLowerCase() === 'body');
            const prevBody = prevBodyKey ? prev[prevBodyKey] : undefined;
            if (prevBody === NON_INDEXED_PROPERTY_STUB || prevBody === undefined) {
                const key = prevBodyKey ?? bodyKey;
                return { ...prev, [key]: bodyValue };
            }
            return prev;
        });
    }, [existingEntity, parentProvidesEntityLoad]);

    const {
        referenceEntitiesMap,
        referenceFetchStatus,
        getReferenceDisplayName,
        updateReferenceEntity,
        clearReferenceEntity
    } = useReferenceFieldDisplay({
        open,
        schemaAttributes,
        formData,
        isSetTransition,
        isPolicyRule,
        isRequestApprovalType,
        entityService,
        initialReferenceEntities: initialReferenceEntitiesFromEntity,
        editingEntityId: entityId ?? null,
        editingEntityType: entityType ?? null
    });

    // Read-only attribute names (tier2 + tier3 Binding.ReadOnly) â€” excluded from create payload
    const readOnlyAttributeNames = useMemo(() => {
        const t2 = getReadOnlyAttributeNamesFromTier2(tier2Entitlement);
        const t3 = getReadOnlyAttributeNamesFromTargetResponse(targetEntitlement ?? null);
        return [...new Set([...t2, ...t3])];
    }, [tier2Entitlement, targetEntitlement]);

    // Use hook for save/apply operations
    const {
        handleSave,
        handleApply,
        buildPatchOperations
    } = useEntitySave(
        {
            entityType,
            entityId,
            isEditMode,
            isPolicyRule,
            isSet,
            isPerson,
            entityLabel,
            relativeTargetAttributeError
        },
        {
            formData,
            originalData,
            schemaAttributes,
            existingEntity,
            encryptedFieldsClearOnSave,
            readOnlyAttributeNames,
            deferredArrayPendingRemoves,
            deferredArrayPendingAdds
        },
        {
            setLoading,
            setError,
            setFormData,
            setOriginalData,
            onSuccess,
            onClose,
            onApplySuccess: () => {
                setDeferredArrayPendingRemoves({});
                setDeferredArrayPendingAdds({});
            }
        },
        entityService,
        userService
    );

    // Load ActionWorkflow display name when value changes
    useEffect(() => {
        const loadActionWorkflowName = async () => {
            if (!isSetTransition) {
                setSelectedActionWorkflow(null);
                setActionWorkflowError(null);
                return;
            }

            // Extract ID from ActionWorkflow (via getEntityValueFromARef)
            // Note: When skipReferenceExpansion=false, stale references (deleted entities) return just the ID string
            const workflowId = extractReferenceId(getEntityValueFromARef('ActionWorkflow', formData));
            if (workflowId) {
                // If we have a reference object with DisplayName, use it temporarily
                const actionWorkflowVal = getEntityValueFromARef('ActionWorkflow', formData);
                if (actionWorkflowVal && typeof actionWorkflowVal === 'object' && (actionWorkflowVal as any).DisplayName) {
                    setSelectedActionWorkflow(actionWorkflowVal);
                    setActionWorkflowError(null);
                    return;
                }
            }

            if (!workflowId) {
                setSelectedActionWorkflow(null);
                setActionWorkflowError(null);
                return;
            }

            try {
                const workflow = await entityService.getEntityById(workflowId, 'workflow');
                if (workflow) {
                    setSelectedActionWorkflow(workflow);
                    setActionWorkflowError(null);
                } else {
                    setSelectedActionWorkflow({ Id: workflowId });
                    setActionWorkflowError('Workflow not found (may have been deleted)');
                }
            } catch (e: any) {
                // Workflow not found or error
                setSelectedActionWorkflow({ Id: workflowId });
                const statusCode = e?.response?.status || e?.status;
                if (statusCode === 404) {
                    setActionWorkflowError('Workflow not found (may have been deleted)');
                } else {
                    setActionWorkflowError(`Error loading workflow: ${e?.message || 'Unknown error'}`);
                }
            }
        };
        loadActionWorkflowName();
    }, [formData, isSetTransition, entityService]);

    useEffect(() => {
        if (!open) {
            setInitialReferenceEntitiesFromEntity({});
            setEmailTemplatesList([]);
            setLoadingEmailTemplates(false);
        }
    }, [open]);

    // PolicyRule: Handle TargetSet change (wraps hook handler + validation)
    const handleTargetSetChange = useCallback((setId: string | null) => {
        handleFieldChange('TargetSet', setId);
        hookHandleTargetSetChange(setId);

        if (setId && getEntityValueFromARef('RelativeTargetAttribute', formData)) {
            // "Id" is always valid - it's a self-referential attribute
            const relativeTargetVal = getEntityValueFromARef('RelativeTargetAttribute', formData);
            const normalizedAttr = (relativeTargetVal != null ? String(relativeTargetVal) : '')?.trim().toLowerCase();
            if (normalizedAttr === 'id') {
                setRelativeTargetAttributeError(null);
                return;
            }

            // Validate RelativeTargetAttribute after a short delay to allow hook to fetch
            fetchTargetSetFilterScope(setId).then(() => {
                const cached = setEntitiesCacheRef.current.get(setId);
                if (cached && cached.filterScope) {
                    const filterScope = cached.filterScope;
                    const state = store.getState();
                    const fromRedux = getFieldOptionsForEntityType(state, filterScope);
                    const cachedTier2 = EntitlementsService.getCachedEntityEntitlementForSchemaEntity(filterScope);
                    const fieldOptions = fromRedux.length > 0 ? fromRedux : (isEntitySetFacade(cachedTier2) ? buildFormAttributesFromTier2Attributes(cachedTier2?.Attributes) : (buildFormAttributesFromTier2AttributeBindings(cachedTier2?.AttributeBindings) ?? buildFormAttributesFromTier2Attributes(cachedTier2?.Attributes)));
                    if (fieldOptions.length === 0) return; // Tier2 not loaded yet for this scope, skip validation
                    const attributeExists = fieldOptions.some(attr => {
                        const attrName = attr.name?.toLowerCase();
                        const attrId = attr.id?.toLowerCase();
                        const selectedAttr = (getEntityValueFromARef('RelativeTargetAttribute', formData) ?? '')?.toString().toLowerCase();
                        return attrName === selectedAttr || attrId === selectedAttr;
                    });
                    if (!attributeExists) {
                        setRelativeTargetAttributeError(
                            `The selected attribute "${getEntityValueFromARef('RelativeTargetAttribute', formData) ?? ''}" does not exist for entity type "${filterScope}". Please clear the attribute or select a different target set.`
                        );
                    } else {
                        setRelativeTargetAttributeError(null);
                    }
                }
            });
        } else if (!setId) {
            setRelativeTargetAttributeError(null);
        }
    }, [formData, hookHandleTargetSetChange, fetchTargetSetFilterScope]);

    // PolicyRule: Handle RequestorSet change (wraps hook handler). RequestorSet and RelativeTargetAttribute are mutually exclusive â€” clear the other when one is set.
    const handleRequestorSetChange = useCallback((setId: string | null) => {
        handleFieldChange('RequestorSet', setId);
        if (setId) handleFieldChange('RelativeTargetAttribute', '');
        hookHandleRequestorSetChange(setId);
    }, [hookHandleRequestorSetChange]);

    // Note: Filter scope loading for initial values is handled by usePolicyRuleSetScopes hook

    const handleFieldChange = (fieldName: string, value: any) => {
        form.setValues((prev) => {
            const next = { ...prev } as Record<string, unknown>;
            setEntityValueFromARef(fieldName, next, value);
            return next;
        });
    };

    /** Normalize FilterScope to a string entity type (handles string or object with DisplayName/displayName/Id/id/name/value). */
    const normalizeFilterScopeToEntityType = useCallback((scopeVal: unknown): string => {
        if (scopeVal == null) return '';
        if (typeof scopeVal === 'string') return scopeVal.trim();
        if (typeof scopeVal === 'object') {
            const o = scopeVal as Record<string, unknown>;
            const s = (o.DisplayName ?? o.displayName ?? o.Id ?? o.id ?? o.name ?? o.value);
            return s != null ? String(s).trim() : '';
        }
        return String(scopeVal).trim();
    }, []);

    /** Set Filter preview: open FilterPreviewDialog for FilterScope + Filter. */
    const handleFilterPreview = useCallback(() => {
        const scopeVal = getEntityValueFromARef('FilterScope', formData);
        const filterScope = normalizeFilterScopeToEntityType(scopeVal);
        const filter = (getEntityValueFromARef('Filter', formData) ?? '').toString().trim();
        if (!filterScope) {
            notifications.show({ message: 'Select a Filter Scope first.', color: 'yellow' });
            return;
        }
        setFilterPreviewScope(filterScope);
        setFilterPreviewFilter(filter);
        setFilterPreviewAttributes(['Id', 'DisplayName', 'UserName', 'EntityType']);
        setFilterPreviewTitleOverride('Filter preview');
        setFilterPreviewNoteMessage(undefined);
        setFilterPreviewOpen(true);
    }, [formData, normalizeFilterScopeToEntityType]);

    /** Group Preview Members (enumerated managed only): FilterPreviewDialog with OnPremId pr filter. */
    const handleGroupPreviewMembers = useCallback(() => {
        const scopeVal = getEntityValueFromARef('FilterScope', formData);
        const filterScope = normalizeFilterScopeToEntityType(scopeVal);
        const filterRaw = (getEntityValueFromARef('Filter', formData) ?? '').toString().trim();
        const effectiveFilter = filterRaw ? `(${filterRaw}) and OnPremId pr` : 'OnPremId pr';
        if (!filterScope) {
            notifications.show({ message: 'Select a Filter Scope first.', color: 'yellow' });
            return;
        }
        setFilterPreviewScope(filterScope);
        setFilterPreviewFilter(effectiveFilter);
        setFilterPreviewAttributes(['Id', 'DisplayName', 'EntityType', 'OnPremId']);
        setFilterPreviewTitleOverride('Preview Members');
        setFilterPreviewNoteMessage(
            'Group members must have OnPremId for previewing. The preview filter includes OnPremId pr.'
        );
        setFilterPreviewOpen(true);
    }, [formData, normalizeFilterScopeToEntityType]);

    const closeFilterPreview = useCallback(() => {
        setFilterPreviewOpen(false);
        setFilterPreviewScope('');
        setFilterPreviewFilter('');
        setFilterPreviewAttributes(undefined);
        setFilterPreviewTitleOverride(undefined);
        setFilterPreviewNoteMessage(undefined);
    }, []);

    // Ref so async callbacks (e.g. FileReader.onload) always call latest handler and trigger re-render/dirty state
    const handleFieldChangeRef = useRef(handleFieldChange);
    handleFieldChangeRef.current = handleFieldChange;

    /** Load a single non-indexed attribute and return its value; form will call onFieldChange with it. */
    const onRequestAttributeValue = useCallback(
        async (attributeName: string): Promise<unknown | null> => {
            if (!entityId || !entityType?.trim()) return null;
            const attrForRequest = (attributeName ?? '').trim().replace(/^\$\./, '') || attributeName;
            setLoadingAttribute(attributeName);
            try {
                const result = await entityService.getEntityById(entityId, entityType.trim(), false, [attrForRequest]);
                if (result && typeof result === 'object') {
                    const candidateKeys = [attributeName, attrForRequest].filter((k) => !!k && k.length > 0);
                    const lowerSet = new Set(candidateKeys.map((k) => k.toLowerCase()));
                    const direct = candidateKeys.find((k) => Object.prototype.hasOwnProperty.call(result, k));
                    const resolved = direct ?? Object.keys(result).find((k) => lowerSet.has((k ?? '').toLowerCase()));
                    const value = resolved !== undefined ? (result as Record<string, unknown>)[resolved] : null;
                    return value ?? null;
                }
                return null;
            } catch (err) {
                console.error('GenericEntityUpsertDialog: failed to load attribute', attributeName, err);
                return null;
            } finally {
                setLoadingAttribute(null);
            }
        },
        [entityId, entityType, entityService]
    );

    // handleSave and handleApply are now provided by useEntitySave hook

    const handleClose = () => {
        if (loading) {
            return; // Don't allow closing while loading
        }

        // Check if there are unsaved changes using centralized hook
        if (!handleCloseAttempt()) {
            return; // Dialog will be shown by hook
        }

        // No unsaved changes, close immediately
        // Move focus out of the dialog before close to avoid "Blocked aria-hidden" (focus must not be hidden from assistive technology)
        if (document.activeElement && document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        setFormData({});
        setOriginalData(null);
        setError(null);
        setActiveTab(0);
        setLoadingAttribute(null);
        setPaginatedArrayRefViewer(null);
        if (isContent) {
            clearContentBodyHashParam();
        }
        requestAnimationFrame(() => {
            onClose();
        });
    };

    const handleConfirmDiscard = () => {
        handleConfirmDiscardBase(() => {
            if (document.activeElement && document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
            }
            setFormData({});
            setOriginalData(null);
            setError(null);
            setActiveTab(0);
            setLoadingAttribute(null);
            if (isContent) {
                clearContentBodyHashParam();
            }
            requestAnimationFrame(() => {
                onClose();
            });
        });
    };

    const payloadDebugFormData = rawViewsFormDataSnapshot;
    const isPayloadDebugDataReady = !isPayloadDebugTabActive || payloadDebugFormData != null;

    // Compute originalData, currentData, and expectedPayload for diff and payload views
    const { originalDataForDiff, currentDataForDiff, expectedPayload } = useMemo(() => {
        if (!isPayloadDebugTabActive || !payloadDebugFormData) {
            return {
                originalDataForDiff: null,
                currentDataForDiff: null,
                expectedPayload: null
            };
        }

        if (!isEditMode || !existingEntity || !originalData) {
            // Create mode - build expected payload (same shape as useEntitySave: plain keys, no $. ; PolicyRule set refs from formData)
            const namePlain = (n: string) => (n ?? '').toString().trim().replace(/^\$\./, '') || (n ?? '');
            const createPayload: any = {};
            schemaAttributes.forEach((attr: any) => {
                const name = attr.name || attr.id || attr.value;
                if (name == null || String(name).trim() === '') return;
                const plain = namePlain(name);
                const attrRefPayload = createEntityRef(name);
                let value = getEntityValueFromARef(name, payloadDebugFormData);
                // PolicyRule: form stores set refs under RequestorSet/TargetSet/TargetPostOpSet; schema may use "$.RequestorSet"
                if (isPolicyRule && value === undefined) {
                    if (isARef(attrRefPayload, 'RequestorSet')) value = getEntityValueFromARef('RequestorSet', payloadDebugFormData);
                    else if (isARef(attrRefPayload, 'TargetSet')) value = getEntityValueFromARef('TargetSet', payloadDebugFormData);
                    else if (isARef(attrRefPayload, 'TargetPostOpSet')) value = getEntityValueFromARef('TargetPostOpSet', payloadDebugFormData);
                }

                if (value !== undefined && value !== null && value !== '') {
                    if (Array.isArray(value) && value.length === 0) {
                        return;
                    }
                    // Multi-valued reference (e.g. Owner): API expects array of ids
                    let payloadValue = value;
                    if (attr.multiValued && attr.isReference && Array.isArray(value)) {
                        payloadValue = value.map((item: any) => {
                            if (item == null) return null;
                            if (typeof item === 'string') return item;
                            return item.Id ?? item.id ?? null;
                        }).filter((id: any) => id != null && id !== '');
                    }
                    // PolicyRule Attributes: keep array of id strings as-is (objects -> extract id only)
                    if (isPolicyRule && isARef(attrRefPayload, 'Attributes') && Array.isArray(value)) {
                        payloadValue = value.map((attrItem: any) => {
                            if (typeof attrItem === 'string') return attrItem;
                            if (attrItem != null && typeof attrItem === 'object') {
                                const id = (attrItem.Id ?? attrItem.id ?? attrItem.DisplayName ?? attrItem.displayName ?? '').toString().trim();
                                return id || null;
                            }
                            return null;
                        }).filter((id: any) => id != null && id !== '');
                    }
                    // PolicyRule set refs: single id string; use plain key (RequestorSet not $.RequestorSet)
                    const outKey = isPolicyRule && isARef(attrRefPayload, 'RequestorSet') ? 'RequestorSet'
                        : isPolicyRule && isARef(attrRefPayload, 'TargetSet') ? 'TargetSet'
                            : isPolicyRule && isARef(attrRefPayload, 'TargetPostOpSet') ? 'TargetPostOpSet'
                                : plain || name;
                    if (isPolicyRule && (isARef(attrRefPayload, 'RequestorSet') || isARef(attrRefPayload, 'TargetSet') || isARef(attrRefPayload, 'TargetPostOpSet'))) {
                        const idVal = extractReferenceId(payloadValue);
                        if (idVal != null && idVal !== '') {
                            createPayload[outKey] = idVal;
                        }
                    } else if (payloadValue !== undefined && payloadValue !== null && payloadValue !== '') {
                        createPayload[outKey] = payloadValue;
                    }
                } else if (attr.type === 'boolean') {
                    createPayload[plain || name] = value || false;
                }
            });
            // PolicyRule: ensure set refs in payload when present in formData (same as useEntitySave)
            if (isPolicyRule) {
                for (const key of ['RequestorSet', 'TargetSet', 'TargetPostOpSet'] as const) {
                    const raw = getEntityValueFromARef(key, payloadDebugFormData);
                    const idVal = extractReferenceId(raw);
                    if (idVal != null && idVal !== '') {
                        createPayload[key] = idVal;
                    }
                }
            }

            return {
                originalDataForDiff: null,
                currentDataForDiff: null,
                expectedPayload: createPayload
            };
        }

        // Edit mode - use hook's normalized operations for Expected Payload tab (refs as Id only; matches API)
        const pathForPatchOp = (n: string) => (n ?? '').toString().trim().replace(/^\$\./, '') || (n ?? '');
        const operations = buildPatchOperations({ formData: payloadDebugFormData });
        const patchPayload = {
            schemas: ['urn:ietf:params:scim:api:messages:2.0:PatchOp'],
            Operations: operations
        };

        // Build diff data - ensure Body is displayed as a readable multiline string
        // Similar to how Config is parsed from JSON string to object for better readability
        const buildDiffData = (data: Record<string, any>) => {
            // Match useEntitySave.buildPatchOperations: normalize refs to id(s) before comparing,
            // and resolve values with getEntityValueFromARef so keys match tier3 $. / DisplayName variants.
            const normalized = normalizeFormDataForPatchComparison(data, schemaAttributes);
            const diffData: Record<string, any> = {};
            schemaAttributes.forEach((attr: any) => {
                const name = getAttributeName(attr);
                if (!name) return;
                const key = pathForPatchOp(name);
                let value = getEntityValueFromARef(name, normalized);

                // Handle Body property - ensure it's a clean string (not JSON-escaped)
                // Body should be displayed as multiline HTML in the diff viewer
                if (isARef(createEntityRef(name), 'Body') && typeof value === 'string') {
                    // If it's a JSON-escaped string (wrapped in quotes), unescape it
                    const trimmed = value.trim();
                    if (trimmed.startsWith('"') && trimmed.endsWith('"') && trimmed.length > 2) {
                        try {
                            // Try to parse as JSON to unescape
                            const parsed = JSON.parse(value);
                            if (typeof parsed === 'string') {
                                value = parsed;
                            }
                        } catch {
                            // Not a JSON-escaped string, keep as-is
                        }
                    }
                    // Body is now a clean HTML string - it will be displayed as multiline
                    // when JSON.stringify formats it (though JSON will escape it, the content is readable)
                }

                diffData[key] = value;
            });
            return diffData;
        };

        return {
            originalDataForDiff: buildDiffData(originalData),
            currentDataForDiff: buildDiffData(payloadDebugFormData),
            expectedPayload: patchPayload
        };
    // buildPatchOperations omitted from deps: we want this memo to re-run when formData/originalData or deferred state change;
    // we call buildPatchOperations() inside so we use the current render's function (correct closure).
    }, [isPayloadDebugTabActive, payloadDebugFormData, isEditMode, existingEntity, originalData, schemaAttributes, buildPatchOperations]);

    const unifiedRawViewsProps: UnifiedRawViewsTabsProps = useMemo(() => ({
        entityLabel,
        expectedPayload,
        formData,
        originalDataForDiff,
        currentDataForDiff,
        debugEntity: existingEntity || fullEntityData || formData,
        entityType,
        targetEntitlement: targetEntitlement ?? null,
        isEmailTemplate,
        isEditMode: !isViewSurface,
    }), [
        entityLabel,
        expectedPayload,
        formData,
        originalDataForDiff,
        currentDataForDiff,
        existingEntity,
        fullEntityData,
        entityType,
        targetEntitlement,
        isEmailTemplate,
        isViewSurface,
    ]);

    // Normalize a value for change detection: treat empty as same, compare references by ID
    const normalizeForCompare = useCallback((val: any): any => {
        if (val === undefined || val === null || val === '') return null;
        if (typeof val === 'object' && val !== null && (val.Id != null || val.id != null)) {
            return extractReferenceId(val) ?? null;
        }
        return val;
    }, []);

    // Check if there are any changes in edit mode
    const hasChanges = useMemo(() => {
        if (!isEditMode || !originalData) {
            // In create mode, always allow save (form validation will handle empty required fields)
            return true;
        }
        // Don't report changes while schema/form is still loading (avoids false positive when form isn't synced yet)
        if (schemaLoading) {
            return false;
        }
        // User chose to clear one or more encrypted fields on save
        if (encryptedFieldsClearOnSave.length > 0) {
            return true;
        }
        // Deferred large array-ref: pending removes or adds (not stored in formData)
        if (Object.keys(deferredArrayPendingRemoves).some((k) => (deferredArrayPendingRemoves[k]?.length ?? 0) > 0)) {
            return true;
        }
        if (Object.keys(deferredArrayPendingAdds).some((k) => (deferredArrayPendingAdds[k]?.length ?? 0) > 0)) {
            return true;
        }

        // Same resolution + ref normalization as buildPatchOperations (see buildDiffData / useEntitySave).
        const normForm = normalizeFormDataForPatchComparison(formData, schemaAttributes);
        const normOrig = normalizeFormDataForPatchComparison(originalData, schemaAttributes);
        for (const attr of schemaAttributes) {
            const name = getAttributeName(attr);
            if (!name) continue;
            const newVal = getEntityValueFromARef(name, normForm);
            const oldVal = getEntityValueFromARef(name, normOrig);

            const newNorm = normalizeForCompare(newVal);
            const oldNorm = normalizeForCompare(oldVal);

            if (JSON.stringify(newNorm) !== JSON.stringify(oldNorm)) {
                return true; // Found a change
            }
        }

        return false; // No changes detected
    }, [isEditMode, originalData, formData, schemaAttributes, schemaLoading, normalizeForCompare, encryptedFieldsClearOnSave, deferredArrayPendingRemoves, deferredArrayPendingAdds]);

    // Validation: split into per-field (show inline with field) vs general (show above action buttons).
    const { fieldWarningsMap, generalWarnings } = useMemo(() => {
        if (!entityType) return { fieldWarningsMap: new Map<string, string>(), generalWarnings: [] };
        const all = getEntityValidationWarnings(entityType, validationFormDataSnapshot, 'edit', {
            isCloneMode: isRequestApprovalType && !isEditMode && !!existingEntity,
            existingEntityDisplayName: existingEntity?.DisplayName
        });
        const fieldMap = new Map<string, string>();
        const general: typeof all = [];
        for (const w of all) {
            if (w.attributeDisplayNames?.length === 1) {
                const key = normalizeKey(w.attributeDisplayNames[0]);
                const existing = fieldMap.get(key);
                fieldMap.set(key, existing ? `${existing}; ${w.message}` : w.message);
            } else {
                general.push(w);
            }
        }
        return { fieldWarningsMap: fieldMap, generalWarnings: general };
    }, [entityType, validationFormDataSnapshot, isRequestApprovalType, isEditMode, existingEntity]);

    const getFieldWarning = useCallback((attr: { name?: string; id?: string; label?: string }) => {
        if (fieldWarningsMap.size === 0) return null;
        const keys = [attr?.label, attr?.name, attr?.id].filter(Boolean).map((k) => normalizeKey(String(k)));
        for (const k of keys) {
            const msg = fieldWarningsMap.get(k);
            if (msg) return msg;
        }
        return null;
    }, [fieldWarningsMap]);

    // Use centralized unsaved changes hook (customHasChanges so deferred pending removes/adds count as changes)
    const {
        hasUnsavedChanges,
        unsavedChangesDialogOpen,
        setUnsavedChangesDialogOpen,
        handleCloseAttempt,
        handleConfirmDiscard: handleConfirmDiscardBase
    } = useUnsavedChanges({
        isEditMode,
        formData,
        originalData,
        schemaAttributes,
        customHasChanges: () => hasChanges
    });

    useEffect(() => {
        const shouldPublishUnsavedState = open && isInlinePresentation && effectiveSurfaceMode !== 'view';
        const detail = shouldPublishUnsavedState
            ? {
                active: true,
                hasUnsavedChanges,
                mode: (isEditMode ? 'edit' : 'create') as 'create' | 'edit',
                entityType: entityLabel
            }
            : {
                active: false,
                hasUnsavedChanges: false,
                mode: 'edit' as const,
                entityType: entityLabel
            };

        if (onUnsavedStateChange) {
            onUnsavedStateChange(detail);
        } else {
            window.dispatchEvent(new CustomEvent('hades:inline-editor-unsaved-state', { detail }));
        }

        return () => {
            const resetDetail = {
                active: false,
                hasUnsavedChanges: false,
                mode: 'edit' as const,
                entityType: entityLabel
            };
            if (onUnsavedStateChange) {
                onUnsavedStateChange(resetDetail);
            } else {
                window.dispatchEvent(new CustomEvent('hades:inline-editor-unsaved-state', { detail: resetDetail }));
            }
        };
    }, [open, isInlinePresentation, effectiveSurfaceMode, hasUnsavedChanges, isEditMode, entityLabel, onUnsavedStateChange]);

    // Policy Rule: true when "Apply to specific attributes" is selected and all selected attributes are single-value (AddValue/RemoveValue only apply to multi-valued attributes)
    const policyRuleOnlySingleValueAttributesSelected = useMemo(() => {
        if (!isPolicyRule || getEntityValueFromARef('AllAttributes', formData) !== false) return false;
        const selected = getEntityValueFromARef('Attributes', formData);
        if (!Array.isArray(selected) || selected.length === 0) return false;
        const scope = targetSetFilterScope;
        if (!scope) return false;
        const tier2 = EntitlementsService.getCachedEntityEntitlementForSchemaEntity(scope);
        const attrs = getTier2AttributesFromResponse(tier2);
        if (!Array.isArray(attrs)) return false;
        const nameToMulti = new Map<string, boolean>();
        attrs.forEach((a: any) => {
            const id = (a.Id ?? a.DisplayName ?? '').toString().trim();
            if (id) nameToMulti.set(id.toLowerCase(), !!a.Array);
        });
        const selectedNames = selected.map((s: any) => (typeof s === 'string' ? s : '').toLowerCase()).filter(Boolean);
        if (selectedNames.length === 0) return false;
        return selectedNames.every((n: string) => !nameToMulti.get(n));
    }, [isPolicyRule, formData, targetSetFilterScope]);

    const fieldDebugContext: FieldDebugTooltipContext = useMemo(() => ({
        rawEntitySnapshot: rawEntitySnapshotRef.current,
        tier2Entitlement,
        tier2EntitlementError,
        targetEntitlement: targetEntitlement ?? null,
        tier3EntitlementError,
        isEditMode,
        tier3Settled,
        referenceFetchStatus,
    }), [
        tier2Entitlement,
        tier2EntitlementError,
        targetEntitlement,
        tier3EntitlementError,
        isEditMode,
        tier3Settled,
        referenceFetchStatus,
    ]);

    const wrapWithFieldDebug = useCallback((
        fieldName: string,
        content: React.ReactNode,
        rendererKey?: string,
        isStubValue?: boolean
    ): React.ReactNode => (
        <FieldWithDebugTooltip
            fieldName={fieldName}
            rendererKey={rendererKey}
            isStubValue={isStubValue}
            showFieldDebug={showFieldDebugInfo}
            debugContext={fieldDebugContext}
        >
            {content}
        </FieldWithDebugTooltip>
    ), [showFieldDebugInfo, fieldDebugContext]);

    const entityUpsertSession = useEntityUpsertSessionValue({
        wrapWithFieldDebug,
        formData,
        existingEntity,
        entityType,
        entityId: entityId ?? null,
        isEditMode,
        isViewSurface,
        loading,
        schemaEntityBindings,
        schemaAttributes,
        nonIndexedHasLengthCountNamesSet,
        deferredArrayRefNamesSet,
        isEmptyForDeferLoad,
        deferredArrayPendingAdds,
        deferredArrayPendingRemoves,
        setPaginatedArrayRefViewer,
        setDeferredArrayAddField,
        onRequestAttributeValue: isEditMode && entityId ? onRequestAttributeValue : undefined,
        loadingAttribute,
        handleFieldChange,
        handleFieldChangeRef,
        isSetTransition,
        selectedActionWorkflow,
        actionWorkflowError,
        setActionWorkflowBrowserOpen,
        formKeyFromAttrName,
        handleGroupPreviewMembers,
        handleFilterPreview,
        attributeRefBrowserField,
        setAttributeRefBrowserField,
        loadingPossessiveScopeTier2,
        tier2Entitlement,
        referenceAttributesFromPossessiveScope,
        referenceAttributesFromTier2,
        possessiveScope,
        isPluginFunction,
        isRequestApprovalType,
        isPolicyRule,
        handleRequestorSetChange,
        handleTargetSetChange,
        targetSetFilterScope,
        relativeTargetAttributeError,
        setRelativeTargetAttributeError,
        policyRuleOnlySingleValueAttributesSelected,
        isContent,
        onContentLinkClick,
        isEmailTemplate,
        isFile,
        isGroup,
        originalData: originalData ?? null,
        setOriginalData,
        getReferenceDisplayName,
        updateReferenceEntity,
        clearReferenceEntity,
        entityService,
        multiValuedRawInputs,
        setMultiValuedRawInputs,
        emailTemplatesList,
        loadingEmailTemplates,
        allEntityTypes,
        entityTypesFromContext,
        disjointedEntityTypes,
        possessiveTargetAttributeKey,
        loadingPossessiveTargetAttributeTier2,
        tier2PossessiveTargetAttributeEntitlement,
        referenceAttributesFromPossessiveTargetAttribute,
        selectedReferenceAttributeInfo,
        actorsAttributeBrowserOpen,
        setActorsAttributeBrowserOpen,
        setPossessiveWipeConfirmPending,
        setPossessiveWipeConfirmOpen,
        attributesFromPossessiveTargetAttributeTier2,
        contentTypesCacheService,
        setError,
        targetEntitlement: targetEntitlement ?? null,
        decryptReveal,
        setDecryptReveal,
        revealedEncryptedFieldNames,
        setRevealedEncryptedFieldNames,
        encryptedFieldsClearOnSave,
        setEncryptedFieldsClearOnSave,
        encryptedCreateVisible,
        setEncryptedCreateVisible,
        encryptedEditStoredVisible,
        setEncryptedEditStoredVisible,
        encryptedViewDecryptedByName,
        setEncryptedViewDecryptedByName,
        encryptedViewPlainVisible,
        setEncryptedViewPlainVisible,
        revealedDecryptedCacheRef,
        multiRefBrowserField,
        setMultiRefBrowserField,
    });

    const renderUpsertFormField = useCallback(
        (attr: FormAttribute, value: unknown, onChange: (value: unknown) => void) =>
            renderEntityUpsertFormField({ attr, value, onChange, renderField: entityUpsertRenderField }),
        []
    );

    const isExternalManagedGroup = entityType?.toLowerCase() === 'group'
        && (String(getEntityValueFromARef('ManagedType', formData) ?? existingEntity?.ManagedType ?? '').toLowerCase() === 'external');
    const writableAttributeIds = getSettableAttributeIdsFromTargetResponse(targetEntitlement ?? null) ?? [];
    const hasWritableAttributes = writableAttributeIds.length > 0;
    const canOpenInlineEdit = !!entityId && !isExternalManagedGroup && (targetEntitlement == null || hasWritableAttributes);
    const surfaceTitle = isViewSurface
        ? `View ${entityLabel}`
        : isEditMode
            ? `Edit ${entityLabel}`
            : `Create ${entityLabel}`;
    const readableDisabledViewStyles = `
[data-unified-view-surface='true'] .mantine-Input-input:disabled,
[data-unified-view-surface='true'] .mantine-Input-input[readonly],
[data-unified-view-surface='true'] textarea:disabled,
[data-unified-view-surface='true'] textarea[readonly],
[data-unified-view-surface='true'] input:disabled,
[data-unified-view-surface='true'] input[readonly] {
    opacity: 1;
    color: var(--mantine-color-text);
    -webkit-text-fill-color: var(--mantine-color-text);
    background-color: var(--mantine-color-default-hover);
    border-color: var(--mantine-color-default-border);
}

[data-unified-view-surface='true'] .mantine-InputWrapper-label,
[data-unified-view-surface='true'] .mantine-InputWrapper-description,
[data-unified-view-surface='true'] .mantine-Checkbox-label,
[data-unified-view-surface='true'] .mantine-Input-section {
    opacity: 1;
}

[data-unified-view-surface='true'] .mantine-Checkbox-input:disabled {
    opacity: 0.85;
}
`;
    const getActionBarBorderStyle = (position: 'top' | 'bottom') => (
        position === 'top'
            ? { borderBottom: '1px solid var(--mantine-color-default-border)' }
            : { borderTop: '1px solid var(--mantine-color-default-border)' }
    );

    const renderInlineActions = (position: 'top' | 'bottom' = 'bottom') => {
        if (!isInlinePresentation) return null;
        if (parentOwnsInlineNavigation && position === 'top') return null;

        if (isViewSurface) {
            if (parentOwnsInlineNavigation) return null;
            return (
                <Box px="md" py="sm" style={getActionBarBorderStyle(position)}>
                    <Group justify="space-between" gap="sm">
                        <Button variant="default" onClick={handleClose} disabled={loading}>
                            Back to List
                        </Button>
                        <Button
                            variant="filled"
                            onClick={() => onSurfaceModeChange?.('edit')}
                            disabled={!canOpenInlineEdit || loading || schemaLoading}
                        >
                            Edit
                        </Button>
                    </Group>
                </Box>
            );
        }

        return (
            <Box px="md" py="sm" style={getActionBarBorderStyle(position)}>
                <DialogErrorAlert error={position === 'bottom' ? error : null} />
                <Group justify={parentOwnsInlineNavigation ? 'flex-end' : 'space-between'} gap="sm" wrap="wrap">
                    {!parentOwnsInlineNavigation && (
                        <Group gap="sm">
                            <Button variant="default" onClick={handleClose} disabled={loading}>
                                Back to List
                            </Button>
                            {entityId && onSurfaceModeChange && !hasChanges && (
                                <Button variant="subtle" onClick={() => onSurfaceModeChange('view')} disabled={loading}>
                                    View Mode
                                </Button>
                            )}
                        </Group>
                    )}
                    <Group gap="sm">
                        {hasChanges && (
                            <Button
                                variant="subtle"
                                color="yellow"
                                onClick={() => {
                                    if (originalData) {
                                        setFormData(originalData);
                                    }
                                    // Discard implies "stop editing" on inline surfaces.
                                    if (entityId && onSurfaceModeChange) {
                                        onSurfaceModeChange('view');
                                    }
                                    setError(null);
                                }}
                                disabled={loading}
                            >
                                Discard Changes
                            </Button>
                        )}
                        {hasChanges && (
                            <Button
                                variant="outline"
                                onClick={() => { void handleApply(); }}
                                loading={loading}
                                disabled={!schemaAttributes.length || schemaLoading || isExternalManagedGroup}
                            >
                                Apply
                            </Button>
                        )}
                        <Button
                            variant="filled"
                            onClick={() => { void (isEditMode && !hasChanges ? Promise.resolve(handleClose()) : handleSave()); }}
                            loading={loading}
                            disabled={loading || schemaLoading || !schemaAttributes.length || isExternalManagedGroup}
                        >
                            {isEditMode ? (hasChanges ? 'Save & Close' : 'Close') : 'Create'}
                        </Button>
                    </Group>
                </Group>
            </Box>
        );
    };

    const renderStandardActions = (position: 'top' | 'bottom' = 'bottom') => (
        <EntityDialogActions
            onCancel={handleClose}
            onSave={handleSave}
            onApply={handleApply}
            onDiscard={() => {
                if (originalData) {
                    setFormData(originalData);
                }
                if (entityId && onSurfaceModeChange) {
                    onSurfaceModeChange('view');
                }
                setError(null);
            }}
            loading={loading}
            isEditMode={isEditMode}
            hasChanges={hasChanges}
            schemaLoading={schemaLoading}
            isValid={schemaAttributes.length > 0}
            error={position === 'bottom' ? error : null}
            disabled={isExternalManagedGroup || isViewSurface}
            borderPosition={position === 'top' ? 'bottom' : 'top'}
        />
    );

    if (isInlinePresentation && !open) {
        return null;
    }

    // Registry-driven custom form layout (replaces 7-branch ternary)
    const sharedLayoutProps = {
        schemaAttributes,
        formData,
        onFieldChange: handleFieldChange,
        renderField: renderUpsertFormField,
        disabled: loading || isViewSurface,
        isStandard: isStandardViewMode,
    };
    const customFormLayout = isPolicyRule ? (
        <PolicyRuleFormLayout
            schemaAttributes={schemaAttributes}
            formData={formData}
            relativeTargetAttributeError={relativeTargetAttributeError}
            renderField={entityUpsertRenderField}
            isStandard={isStandardViewMode}
        />
    ) : isContent ? (
        <ContentCompactLayout
            {...sharedLayoutProps}
            nonIndexedStubValue={NON_INDEXED_PROPERTY_STUB}
            onRequestAttributeValue={isEditMode && entityId ? onRequestAttributeValue : undefined}
            loadingAttribute={loadingAttribute}
        />
    ) : isEmailTemplate ? (
        <EmailTemplateFormLayout {...sharedLayoutProps} />
    ) : isSet ? (
        <SetFormLayout formData={formData} setFormData={setFormData} disabled={loading || isViewSurface} isStandard={isStandardViewMode} />
    ) : isPerson ? (
        <PersonFormLayout {...sharedLayoutProps} />
    ) : isGroup ? (
        <GroupFormLayout {...sharedLayoutProps} />
    ) : isRequestApprovalType ? (
        <RequestApprovalTypeFormLayout {...sharedLayoutProps} />
    ) : undefined;

    const surfaceContent = (
        <EntityUpsertSessionProvider value={entityUpsertSession}>
        <Box pt="md">
            <Box px="md">
                    <DialogErrorAlert error={error} />
                    {entityType?.toLowerCase() === 'group' && (String(getEntityValueFromARef('ManagedType', formData) ?? existingEntity?.ManagedType ?? '').toLowerCase() === 'external') && (
                        <Alert color="blue" mb="md">
                            <Text size="sm" fw={600} mb="xs">ActiveDirectory Managed Group</Text>
                            <Text size="sm">
                                This group is managed by ActiveDirectory (ManagedType: external). All fields are read-only and cannot be edited.
                            </Text>
                        </Alert>
                    )}
                    <Box mx="-md">
                        {isInlinePresentation ? renderInlineActions('top') : renderStandardActions('top')}
                    </Box>
                    <Tabs value={String(activeTab)} onChange={handlePrimaryTabChange} keepMounted={false}>
                        <Tabs.List>
                            <Tabs.Tab value="0">Form</Tabs.Tab>
                            {!isStandardViewMode && showDebugTabs && <Tabs.Tab value="1">Ent</Tabs.Tab>}
                            {!isStandardViewMode && showDebugTabs && (isEditMode ? (
                                <Tabs.Tab value="2">Raw Views</Tabs.Tab>
                            ) : (
                                <>
                                    <Tabs.Tab value="2">Expected Payload</Tabs.Tab>
                                    {existingEntity && !entityId && <Tabs.Tab value="3">Raw Views</Tabs.Tab>}
                                </>
                            ))}
                            {supplementalTabs.map((tab, index) => (
                                <Tabs.Tab
                                    key={tab.key ?? `supplemental-tab-${index}`}
                                    value={String(baseTabCount + index)}
                                    leftSection={tab.leftSection}
                                >
                                    {tab.label}
                                </Tabs.Tab>
                            ))}
                            {!isStandardViewMode && (
                                <Tooltip label={showDebugTabs ? 'Hide debug tabs' : 'Show debug tabs'} withArrow>
                                    <ActionIcon
                                        variant={showDebugTabs ? 'light' : 'subtle'}
                                        size="sm"
                                        ml="xs"
                                        mt={4}
                                        onClick={() => {
                                            setShowDebugTabs(prev => {
                                                if (prev && (activeTab === 1 || activeTab === 2 || activeTab === 3)) {
                                                    setActiveTab(0);
                                                }
                                                return !prev;
                                            });
                                        }}
                                    >
                                        <IconBug size={14} />
                                    </ActionIcon>
                                </Tooltip>
                            )}
                        </Tabs.List>
                        <Box h="sm" />

                        <Tabs.Panel value="0">
                            <Box>
                                {isViewSurface && isInlinePresentation && schemaAttributes.length > 0 && !schemaLoading ? (
                                    <ShadcnDetailViewLayout
                                        schemaAttributes={schemaAttributes}
                                        formData={formData}
                                        entityType={entityType ?? ''}
                                        isStandard={isStandardViewMode}
                                    />
                                ) : (
                                    <EntityUpsertForm
                                        schemaAttributes={schemaAttributes}
                                        formData={formData}
                                        onFieldChange={handleFieldChange}
                                        loading={schemaLoading || (open && schemaAttributes.length === 0)}
                                        entityLabel={entityLabel ?? getSingularLabel(entityType ?? '')}
                                        entityType={entityType ?? undefined}
                                        formKeyFromAttrName={formKeyFromAttrName}
                                        layout={isPluginFunction ? 'possessive-group' : 'default'}
                                        legacySectionHeading={entityType?.toLowerCase() === 'group' ? (
                                            <Text component="div" fw={600} size="sm" mt="lg" mb="xs" c="dimmed">Legacy</Text>
                                        ) : undefined}
                                        possessiveGroupNames={
                                            isPluginFunction
                                                ? ['possessivescope', 'possessivetargetattribute', 'possessiveactorsattributes']
                                                : []
                                        }
                                        getFieldWarning={getFieldWarning}
                                        customLayout={customFormLayout}
                                        renderField={renderUpsertFormField}
                                        showFieldDebug={showFieldDebugInfo}
                                        onToggleFieldDebug={() => setShowFieldDebugInfo((v) => !v)}
                                        disabled={loading || isViewSurface}
                                        nonIndexedStubValue={NON_INDEXED_PROPERTY_STUB}
                                        onRequestAttributeValue={isEditMode && entityId ? onRequestAttributeValue : undefined}
                                        loadingAttribute={loadingAttribute}
                                    />
                                )}
                            </Box>
                        </Tabs.Panel>

                        <Tabs.Panel value="1">
                            <EntitlementViewer
                                tier1Data={EntitlementsService.getCachedEntityEntitlements()}
                                tier2Data={entTier2Resolved.data}
                                tier2Loading={entTier2Resolved.loading}
                                tier2Error={entTier2Resolved.error}
                                tier2TypesInPlay={entTier2TypesInPlay}
                                tier2SelectedEntityType={entTier2SelectedEntityType}
                                onTier2EntityTypeChange={setEntTier2SelectedEntityType}
                                currentEntityTypeKey={currentEntityTypeKey}
                                entityTypeOptions={entityTypesFromContext ?? []}
                                targetEntitlement={targetEntitlement}
                                entityType={entityType ?? ''}
                                entityId={entityId ?? null}
                                tier3Error={tier3EntitlementError}
                            />
                        </Tabs.Panel>

                        <Tabs.Panel value="2">
                            {!isEditMode ? (
                                <Box>
                                    {!isPayloadDebugDataReady ? (
                                        <Box p="md">
                                            <Group gap="sm">
                                                <Loader size="sm" />
                                                <Text size="sm" c="dimmed">Preparing payload preview...</Text>
                                            </Group>
                                        </Box>
                                    ) : (
                                        <Tabs defaultValue="payload" keepMounted={false}>
                                            <Tabs.List>
                                                <Tabs.Tab value="payload">Expected Payload</Tabs.Tab>
                                                <Tabs.Tab value="formdata">FormData</Tabs.Tab>
                                            </Tabs.List>
                                            <Tabs.Panel value="payload">
                                                <JsonViewer
                                                    data={expectedPayload}
                                                    title="Expected CREATE Payload"
                                                    description={`This is the CREATE request that will be sent to create the ${entityLabel.toLowerCase()}.`}
                                                    formatBodyMultiline
                                                />
                                            </Tabs.Panel>
                                            <Tabs.Panel value="formdata">
                                                <JsonViewer
                                                    data={formData}
                                                    title="FormData"
                                                    description="Raw form state (keys and values as stored in the dialog)."
                                                    formatBodyMultiline
                                                />
                                            </Tabs.Panel>
                                        </Tabs>
                                    )}
                                </Box>
                            ) : isEditMode ? (
                                <Box>
                                    {!isPayloadDebugDataReady ? (
                                        <Box p="md">
                                            <Group gap="sm">
                                                <Loader size="sm" />
                                                <Text size="sm" c="dimmed">Preparing raw views...</Text>
                                            </Group>
                                        </Box>
                                    ) : (
                                        <UnifiedRawViewsTabs {...unifiedRawViewsProps} />
                                    )}
                                </Box>
                            ) : null}
                        </Tabs.Panel>

                        <Tabs.Panel value="3">
                            {!isEditMode && existingEntity && !entityId ? (
                                <Box>
                                    {/* Source Raw JSON Section */}
                                    <Box mb="md">
                                        <JsonViewer
                                            data={existingEntity}
                                            title="Source Raw JSON (Cloned From)"
                                            description={`This is the original ${entityLabel.toLowerCase()} that is being cloned.`}
                                            formatBodyMultiline
                                        />
                                    </Box>

                                    {/* Raw Data Section */}
                                    <Box>
                                        <SharedDebugComponent
                                            entity={existingEntity}
                                            entityType={entityType}
                                        />
                                    </Box>
                                </Box>
                            ) : null}
                        </Tabs.Panel>
                        {supplementalTabs.map((tab, index) => (
                            <Tabs.Panel
                                key={tab.key ?? `supplemental-panel-${index}`}
                                value={String(baseTabCount + index)}
                            >
                                {tab.content}
                            </Tabs.Panel>
                        ))}
                    </Tabs>
                    {generalWarnings.length > 0 && (
                        <Box pb="xs" pt="md">
                            {generalWarnings.map((w, i) => (
                                <Alert key={i} color="yellow" mb="sm" title={w.severity === 'warning' ? 'Warning' : undefined}>
                                    <Text size="sm">{w.message}</Text>
                                    {w.attributeDisplayNames && w.attributeDisplayNames.length > 0 && (
                                        <Text size="xs" c="dimmed" mt="xs">Affected fields: {w.attributeDisplayNames.join(', ')}</Text>
                                    )}
                                </Alert>
                            ))}
                        </Box>
                    )}
            </Box>
            {isInlinePresentation ? renderInlineActions() : (
                <>
                    {renderStandardActions('bottom')}
                </>
            )}
        </Box>
        </EntityUpsertSessionProvider>
    );

    return (
        <>
            {isInlinePresentation ? (
                <Paper
                    withBorder
                    radius="md"
                    shadow="sm"
                    mt="md"
                    data-unified-view-surface={isViewSurface ? 'true' : undefined}
                >
                    {isViewSurface && <style>{readableDisabledViewStyles}</style>}
                    <Box px="md" pt="md">
                        <Tooltip label="GenericEntityUpsertDialog" withArrow position="top">
                            <span style={{ cursor: 'help' }}>
                                <Text fw={600} size="lg" component="span">{surfaceTitle}</Text>
                            </span>
                        </Tooltip>
                    </Box>
                    {surfaceContent}
                </Paper>
            ) : (
                <Modal
                    opened={open}
                    onClose={handleClose}
                    size={modalFs.size}
                    fullScreen={modalFs.fullScreen}
                    title={
                        <Tooltip label="GenericEntityUpsertDialog" withArrow position="top">
                            <span style={{ cursor: 'help' }}>
                                <Text fw={600} size="lg" component="span">{surfaceTitle}</Text>
                            </span>
                        </Tooltip>
                    }
                >
                    {surfaceContent}
                </Modal>
            )}

            {/* Unsaved Changes Confirmation Dialog */}
            <ShadcnUnsavedChangesDialog
                open={unsavedChangesDialogOpen}
                onClose={() => setUnsavedChangesDialogOpen(false)}
                onConfirm={handleConfirmDiscard}
                mode={isEditMode ? 'edit' : 'create'}
                entityType={entityLabel}
            />

            <PossessiveTypeWipeConfirmDialog
                open={possessiveWipeConfirmOpen}
                onClose={() => { setPossessiveWipeConfirmOpen(false); setPossessiveWipeConfirmPending(null); }}
                onConfirm={() => {
                    if (possessiveWipeConfirmPending != null) {
                        handleFieldChange('PossessiveScope', '');
                        handleFieldChange('PossessiveTargetAttribute', '');
                        handleFieldChange('PossessiveActorsAttribute', '');
                        handleFieldChange('PossessiveActorsAttributes', '');
                        handleFieldChange('Type', possessiveWipeConfirmPending);
                    }
                    setPossessiveWipeConfirmOpen(false);
                    setPossessiveWipeConfirmPending(null);
                }}
            />

            {/* Entity Browser Dialog for ActionWorkflow selection */}
            {isSetTransition && (
                <EntityBrowserDialog
                    open={actionWorkflowBrowserOpen}
                    onClose={() => setActionWorkflowBrowserOpen(false)}
                    onConfirm={(selectedEntities) => {
                        if (selectedEntities.length > 0) {
                            const entity = selectedEntities[0];
                            const ref = { Id: entity.Id, DisplayName: entity.DisplayName, EntityType: entity.EntityType };
                            handleFieldChange('ActionWorkflow', ref);
                            setSelectedActionWorkflow(ref);
                            setActionWorkflowError(null);
                        }
                        setActionWorkflowBrowserOpen(false);
                    }}
                    entityType="workflow"
                    title="Select Action Workflow"
                    callerName="GenericEntityUpsertDialog (ActionWorkflow)"
                    singleSelection={true}
                    scimFilter='Type eq "Action"'
                />
            )}

            <EntityUpsertReferenceBrowsers
                multiRefBrowserField={multiRefBrowserField}
                onCloseMultiRef={() => setMultiRefBrowserField(null)}
                deferredArrayAddField={deferredArrayAddField}
                onCloseDeferredAdd={() => setDeferredArrayAddField(null)}
                schemaAttributes={schemaAttributes}
                formData={formData}
                isSet={isSet}
                manualMemberAllowedTypes={manualMemberAllowedTypes}
                onMultiRefConfirm={(field, nextRefs) => {
                    handleFieldChange(field, nextRefs);
                    setMultiRefBrowserField(null);
                }}
                onDeferredAddConfirm={(field, toAdd) => {
                    if (toAdd.length > 0) {
                        setDeferredArrayPendingAdds((prev) => ({
                            ...prev,
                            [field]: [...(prev[field] ?? []), ...toAdd],
                        }));
                    }
                    setDeferredArrayAddField(null);
                }}
            />

            <FilterPreviewDialog
                open={filterPreviewOpen}
                onClose={closeFilterPreview}
                filter={filterPreviewFilter}
                filterScope={filterPreviewScope}
                entityType={filterPreviewScope}
                titleOverride={filterPreviewTitleOverride}
                noteMessage={filterPreviewNoteMessage}
                attributes={filterPreviewAttributes}
            />

            <PaginatedArrayRefViewerModal
                viewer={paginatedArrayRefViewer}
                schemaAttributes={schemaAttributes}
                viewerEntities={viewerEntities}
                viewerLoading={viewerLoading}
                viewerError={viewerError}
                viewerPage={viewerPage}
                viewerTotalPages={viewerTotalPages}
                viewerTotalResults={viewerTotalResults}
                viewerSelectedIds={viewerSelectedIds}
                viewerSelectedEntities={viewerSelectedEntities}
                viewerExtraColumnAttributes={viewerExtraColumnAttributes}
                onPageChange={setViewerPage}
                onEntitySelect={handleViewerEntitySelect}
                onSelectAll={handleViewerSelectAll}
                onRemoveSelected={(field) => {
                    setDeferredArrayPendingRemoves((prev) => ({
                        ...prev,
                        [field]: [...(prev[field] ?? []), ...viewerSelectedIds],
                    }));
                }}
                onClearSelection={() => {
                    setViewerSelectedIds([]);
                    setViewerSelectedEntities([]);
                }}
                onClose={closeViewer}
            />

        </>
    );
};

export default GenericEntityUpsertDialog;

